import { downloadBlob } from './fileDownload';

const SHEET_NAME_LIMIT = 31;
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const SHEET_NAME_FORBIDDEN = /[\\/?*[\]:]/g;
const FILE_NAME_FORBIDDEN = /[\\/:*?"<>|]/g;
const SHEET_NAME_RESERVED = 'history';
const COLUMN_WIDTH_CAP = 50;

export interface WorkbookSheet {
  name: string;
  rows: string[][];
}

const cleanSheetName = (name: string): string =>
  name
    .replace(SHEET_NAME_FORBIDDEN, ' ')
    .trim()
    .replace(/^'+|'+$/g, '')
    .trim();

const fitSheetName = (name: string, limit: number): string => cleanSheetName(cleanSheetName(name).slice(0, limit));

/** Turns tab names into valid, distinct Excel sheet names: Excel rejects empty names, over 31 characters, \ / ? * [ ] :, edge apostrophes, "History" and duplicates in any case. */
export const sheetNames = (tabNames: string[]): string[] => {
  const taken = new Set<string>();
  return tabNames.map((raw, index) => {
    const base = fitSheetName(raw, SHEET_NAME_LIMIT) || `Sheet ${index + 1}`;
    let candidate = base;
    for (
      let attempt = 2;
      taken.has(candidate.toLowerCase()) || candidate.toLowerCase() === SHEET_NAME_RESERVED;
      attempt += 1
    ) {
      const suffix = ` (${attempt})`;
      candidate = fitSheetName(base, SHEET_NAME_LIMIT - suffix.length) + suffix;
    }
    taken.add(candidate.toLowerCase());
    return candidate;
  });
};

/** Names the workbook after the report and its run date, without the characters a file name cannot hold. */
export const workbookFileName = (reportName: string, runDate: string): string => {
  const printable = [...reportName].filter((char) => char.charCodeAt(0) >= 32).join('');
  const name = printable.replace(FILE_NAME_FORBIDDEN, ' ').replace(/\s+/g, ' ').trim() || 'Report';
  return runDate ? `${name} – ${runDate}.xlsx` : `${name}.xlsx`;
};

/** Reads CSV text, quoted fields and line breaks inside them included, into rows of cells. */
export const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let fieldQuoted = false;
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  for (let i = 0; i < source.length; i += 1) {
    const char = source[i];
    if (quoted) {
      if (char === '"' && source[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      quoted = true;
      fieldQuoted = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
      fieldQuoted = false;
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && source[i + 1] === '\n') i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      fieldQuoted = false;
    } else {
      field += char;
    }
  }
  if (field !== '' || fieldQuoted || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
};

/** Pads rows back up to the grid's row count, because MUI trims its CSV and drops trailing empty rows. */
export const restoreTrailingRows = (rows: string[][], dataRowCount: number): string[][] => {
  if (rows.length === 0) return rows;
  const missing = dataRowCount - (rows.length - 1);
  if (missing <= 0) return rows;
  return [...rows, ...Array.from({ length: missing }, () => Array<string>(rows[0].length).fill(''))];
};

/** Builds one workbook with a sheet per entry and downloads it. ExcelJS is large, so it loads on demand. */
export const downloadWorkbook = async (fileName: string, sheets: WorkbookSheet[]): Promise<void> => {
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook();

  sheets.forEach(({ name, rows }) => {
    const sheet = workbook.addWorksheet(name);
    sheet.addRows(rows);
    sheet.getRow(1).font = { bold: true };
    (sheet.columns ?? []).forEach((column, index) => {
      const longest = rows.reduce((max, row) => Math.max(max, (row[index] ?? '').length), 0);
      column.width = Math.min(Math.max(longest + 2, 10), COLUMN_WIDTH_CAP);
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(new Blob([buffer], { type: XLSX_MIME }), fileName);
};
